import { gql } from "@apollo/client";
import client from "../apolloClient";
import type {
  BrandReport,
  BrandReportCreativeOption,
  BrandReportDownload,
  BrandReportFilter,
  BrandReportScreensPage,
  ReportFormat,
} from "../../schemas/brandReportSchema";

const GET_BRAND_REPORT = gql`
  query GetBrandReport($filter: BrandReportFilter!) {
    brandReport(filter: $filter) {
      brandName
      startDate
      endDate
      summary {
        plays
        airtime
        screens
        restaurants
        cities
        creatives
        activeDays
        avgPlaysPerScreenDay
      }
      daily {
        date
        plays
        airtime
        screens
      }
      hourly {
        dayOfWeek
        hour
        plays
      }
      byCreative {
        creativeId
        name
        thumbnailUrl
        fileType
        startDate
        endDate
        plays
        airtime
        screens
        lastPlayedAt
      }
      byLocation {
        cityId
        city
        regionId
        region
        plays
        airtime
        screens
        restaurants
      }
    }
  }
`;

const GET_BRAND_REPORT_SCREENS = gql`
  query GetBrandReportScreens(
    $filter: BrandReportFilter!
    $search: String
    $limit: Int
    $offset: Int
  ) {
    brandReportScreens(
      filter: $filter
      search: $search
      limit: $limit
      offset: $offset
    ) {
      items {
        screenId
        screenName
        restaurantName
        city
        region
        plays
        airtime
        creatives
        lastPlayedAt
      }
      totalCount
    }
  }
`;

const GET_BRAND_REPORT_CREATIVES = gql`
  query GetBrandReportCreatives {
    brandReportCreatives {
      id
      name
      thumbnailUrl
      isDeleted
    }
  }
`;

const DOWNLOAD_BRAND_REPORT = gql`
  query DownloadBrandReport($input: DownloadBrandReportInput!) {
    downloadBrandReport(input: $input) {
      base64
      fileName
      mimeType
    }
  }
`;

const SEND_BRAND_REPORT_EMAIL = gql`
  mutation SendBrandReportEmail($input: SendBrandReportEmailInput!) {
    sendBrandReportEmail(input: $input)
  }
`;

/** An empty selection means "all", which the server reads as no filter. */
const toFilterVariables = (filter: BrandReportFilter) => ({
  startDate: filter.startDate,
  endDate: filter.endDate,
  creativeIds: filter.creativeIds?.length ? filter.creativeIds : undefined,
  cityId: filter.cityId || undefined,
  regionId: filter.regionId || undefined,
});

/**
 * Scoped to the signed-in brand by the backend from the verified token, like
 * campaignService — no brandId goes on the wire from the brand portal.
 */
export const brandReportService = {
  getReport: async (filter: BrandReportFilter): Promise<BrandReport> => {
    const { data } = await client.query({
      query: GET_BRAND_REPORT,
      variables: { filter: toFilterVariables(filter) },
      fetchPolicy: "network-only",
    });
    return data.brandReport;
  },

  getScreens: async (
    filter: BrandReportFilter,
    params: { search?: string; limit?: number; offset?: number } = {},
  ): Promise<BrandReportScreensPage> => {
    const { data } = await client.query({
      query: GET_BRAND_REPORT_SCREENS,
      variables: { filter: toFilterVariables(filter), ...params },
      fetchPolicy: "network-only",
    });
    return data.brandReportScreens;
  },

  getCreatives: async (): Promise<BrandReportCreativeOption[]> => {
    const { data } = await client.query({
      query: GET_BRAND_REPORT_CREATIVES,
      fetchPolicy: "network-only",
    });
    return data.brandReportCreatives ?? [];
  },

  download: async (
    filter: BrandReportFilter,
    format: ReportFormat,
  ): Promise<BrandReportDownload> => {
    const { data } = await client.query({
      query: DOWNLOAD_BRAND_REPORT,
      variables: { input: { ...toFilterVariables(filter), format } },
      fetchPolicy: "network-only",
    });
    return data.downloadBrandReport;
  },

  sendEmail: async (
    filter: BrandReportFilter,
    email: string,
    format: ReportFormat,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: SEND_BRAND_REPORT_EMAIL,
      variables: { input: { ...toFilterVariables(filter), email, format } },
    });
    return Boolean(data?.sendBrandReportEmail);
  },
};
